import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { answerGiftDecision, getGiftDecision, isOk, messageOf } from "../../services/gift";
import GiftShell, { Check } from "./components/GiftShell";
import "./gift.css";

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="8" x2="12" y2="12.5" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function Result({ tone, title, children }) {
  return (
    <div className="vsr-center">
      <section className="vsr-card vsr-reserved">
        <span className={`vsr-icon-badge ${tone === "success" ? "vsr-icon-badge-success" : ""}`} aria-hidden="true">
          {tone === "success" ? <Check /> : <InfoIcon />}
        </span>
        <h1 className="vsr-title">{title}</h1>
        {children}
        <div className="vsr-actions">
          {localStorage.getItem("access_token") && (
            <a className="vsr-btn" href="/my-gifts">
              View My Gifts
            </a>
          )}
          <a className="vsr-btn vsr-btn-outline" href="https://verysoul.com/">
            Explore VerySoul
          </a>
        </div>
      </section>
    </div>
  );
}

// "Do you still want to give a gift?" — opened from the purchaser's email
// (/gift-decision/:token?answer=yes|no). The link only pre-selects the
// answer; nothing changes until the purchaser confirms.
export default function GiftDecision() {
  const { token } = useParams();
  const location = useLocation();
  const preset = new URLSearchParams(location.search).get("answer");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [choice, setChoice] = useState(preset === "yes" || preset === "no" ? preset : "");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    getGiftDecision(token).then((res) => {
      if (isOk(res)) {
        setInfo(res.data.data);
      } else {
        setError(messageOf(res, "This link is not valid or has expired."));
      }
    });
  }, [token]);

  const confirm = async () => {
    if (!choice) return;
    setBusy(true);
    const res = await answerGiftDecision(token, choice === "yes");
    setBusy(false);
    if (res && res.data && res.data.data && res.data.data.state) {
      setInfo(res.data.data);
    }
    if (!isOk(res)) {
      setMessage(messageOf(res, "We couldn't save your answer. Please try again."));
    }
  };

  let body;
  if (error) {
    body = (
      <Result title="This link has expired">
        <p className="vsr-lead">{error}</p>
        <div className="vsr-note">
          <p>You can still see and manage your gifts from My Gifts in your VerySoul account.</p>
        </div>
      </Result>
    );
  } else if (!info) {
    body = <p className="vsr-muted vsr-loading">Loading…</p>;
  } else if (info.state === "kept") {
    body = (
      <Result tone="success" title="Your gift is active again">
        <p className="vsr-lead">We've let your recipient know they can choose a new time.</p>
        <div className="vsr-note">
          <p>You'll only pay if they book a session — we'll email you when they do.</p>
        </div>
      </Result>
    );
  } else if (info.state === "closed") {
    body = (
      <Result title="Your gift has been closed">
        <p className="vsr-lead">The Gift Code and link no longer work. Nothing else will be charged.</p>
        {message && (
          <div className="vsr-note">
            <p>{message}</p>
          </div>
        )}
      </Result>
    );
  } else if (info.state !== "awaiting") {
    body = (
      <Result title="This gift isn't waiting for an answer">
        <p className="vsr-lead">There's nothing you need to do here.</p>
      </Result>
    );
  } else {
    body = (
      <div className="vsr-center">
        <section className="vsr-card vsr-reserved">
          <span className="vsr-icon-badge" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <rect x="3" y="8" width="18" height="13" rx="2" />
              <path d="M12 8v13M3 12h18M12 8c-1.5-3-5-3.5-5-1.25S9.5 8 12 8zM12 8c1.5-3 5-3.5 5-1.25S14.5 8 12 8z" />
            </svg>
          </span>
          <p className="vsr-eyebrow">VerySoul Gift</p>
          <h1 className="vsr-title">Do you still want to give a gift?</h1>
          <p className="vsr-lead">
            The session requested{info.recipientEmail ? <> by <b>{info.recipientEmail}</b></> : null}
            {info.mediumName ? <> with <b>{info.mediumName}</b></> : null}
            {info.date ? ` on ${info.date} at ${info.time}` : ""} wasn't paid for in time, so the reservation has expired.
          </p>

          <div className="vsr-decision-choices" role="radiogroup" aria-label="Your answer">
            <button
              type="button"
              role="radio"
              aria-checked={choice === "yes"}
              className={`vsr-decision-choice ${choice === "yes" ? "is-selected" : ""}`}
              onClick={() => setChoice("yes")}
            >
              <span className="vsr-decision-choice-title">Yes, keep my gift</span>
              <span className="vsr-decision-choice-text">
                The Gift Code works again and we'll invite your recipient to choose a new time. You'll only pay if they book.
              </span>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={choice === "no"}
              className={`vsr-decision-choice is-no ${choice === "no" ? "is-selected" : ""}`}
              onClick={() => setChoice("no")}
            >
              <span className="vsr-decision-choice-title">No, close it</span>
              <span className="vsr-decision-choice-text">The Gift Code and link stop working. Nothing else is charged.</span>
            </button>
          </div>

          {message && <p className="vsr-error vsr-error-block">{message}</p>}

          <div className="vsr-actions">
            <button
              type="button"
              className={`vsr-btn ${choice === "no" ? "vsr-btn-danger" : ""}`}
              disabled={!choice || busy}
              onClick={confirm}
            >
              {busy ? "Saving…" : choice === "no" ? "Confirm: close my gift" : choice === "yes" ? "Confirm: keep my gift" : "Choose an answer"}
            </button>
          </div>
          {info.closesOn && (
            <p className="vsr-hint vsr-decision-deadline">If we don't hear from you by {info.closesOn}, your gift will close automatically.</p>
          )}
        </section>
      </div>
    );
  }

  return <GiftShell>{body}</GiftShell>;
}
